import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchSessionXml } from '../api/client'
import {
  serializeXml,
  resolveXPath,
  getElementPath,
  getAncestorPaths,
  findBase64ElementPaths,
} from '../utils/xmlViewerUtils'

type DocType = 'ordrsp' | 'desadv' | 'invoice'

const DOC_LABELS: Record<DocType, string> = {
  ordrsp: 'OrderResponse',
  desadv: 'DespatchAdvice',
  invoice: 'Invoice',
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>
  const q = query.toLowerCase()
  const parts: React.ReactNode[] = []
  let lastIdx = 0

  for (let from = 0; ; ) {
    const idx = text.toLowerCase().indexOf(q, from)
    if (idx === -1) { parts.push(text.slice(lastIdx)); break }
    if (idx > lastIdx) parts.push(text.slice(lastIdx, idx))
    parts.push(
      <mark key={idx} className="bg-yellow-300 text-gray-900 rounded-sm not-italic">
        {text.slice(idx, idx + query.length)}
      </mark>
    )
    lastIdx = idx + query.length
    from    = lastIdx
  }
  return <>{parts}</>
}

interface XmlPanelProps {
  sessionId: number
  docType: DocType
  xpath: string | null
  scrollTrigger: unknown
  maxHeight?: string
}

export function XmlPanel({ sessionId, docType, xpath, scrollTrigger, maxHeight = '420px' }: XmlPanelProps) {
  const { t } = useTranslation()
  const [xml, setXml]         = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const [rawMode, setRawMode] = useState(false)

  const [collapsed, setCollapsed]       = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery]   = useState('')
  const [currentMatch, setCurrentMatch] = useState(0)

  const targetScrollRef = useRef<HTMLSpanElement>(null)
  const matchScrollRef  = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchSessionXml(sessionId, docType)
      .then(setXml)
      .catch(e => setError(e instanceof Error ? e.message : t('xml_panel.error_loading')))
      .finally(() => setLoading(false))
  }, [sessionId, docType])

  const [parsedDoc, targetNode] = useMemo(() => {
    if (!xml) return [null, null] as const
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xml, 'text/xml')
    if (xmlDoc.querySelector('parseerror')) return [null, null] as const
    const target = xpath ? resolveXPath(xmlDoc, xpath) : null
    return [xmlDoc, target] as const
  }, [xml, xpath])

  const base64Paths = useMemo(() => {
    if (!parsedDoc) return [] as string[]
    return findBase64ElementPaths(parsedDoc)
  }, [parsedDoc])

  useEffect(() => {
    if (base64Paths.length > 0) {
      setCollapsed(new Set(base64Paths))
    }
  }, [base64Paths])

  const rawLines = useMemo(() => (xml ? xml.split('\n') : []), [xml])

  const xmlLines = useMemo(() => {
    if (rawMode || !parsedDoc) return []
    // During search, expand everything except base64 blobs (they'd be huge and irrelevant)
    const effectiveCollapsed = searchQuery ? new Set<string>(base64Paths) : collapsed
    return serializeXml(parsedDoc, targetNode, effectiveCollapsed)
  }, [rawMode, parsedDoc, targetNode, collapsed, searchQuery, base64Paths])

  const firstTargetIdx = xmlLines.findIndex(l => l.isTarget)

  const matchIndices = useMemo(() => {
    if (rawMode || !searchQuery) return []
    const q = searchQuery.toLowerCase()
    return xmlLines.map((l, i) => ({ l, i })).filter(({ l }) => l.text.toLowerCase().includes(q)).map(({ i }) => i)
  }, [rawMode, searchQuery, xmlLines])

  const rawMatchIndices = useMemo(() => {
    if (!rawMode || !searchQuery) return []
    const q = searchQuery.toLowerCase()
    return rawLines.map((_, i) => i).filter(i => rawLines[i].toLowerCase().includes(q))
  }, [rawMode, searchQuery, rawLines])

  const activeMatchIndices = rawMode ? rawMatchIndices : matchIndices
  const safeMatch = activeMatchIndices.length > 0 ? Math.min(currentMatch, activeMatchIndices.length - 1) : 0

  const toggleCollapse = useCallback((nodeId: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(nodeId)) { next.delete(nodeId) } else { next.add(nodeId) }
      return next
    })
  }, [])

  useEffect(() => {
    if (!parsedDoc || !targetNode) return
    const path      = getElementPath(parsedDoc, targetNode)
    const ancestors = getAncestorPaths(path)
    setCollapsed(prev => {
      const hasCollapsed = ancestors.some(a => prev.has(a))
      if (!hasCollapsed) return prev
      const next = new Set(prev)
      ancestors.forEach(a => next.delete(a))
      return next
    })
  }, [targetNode, parsedDoc])

  useEffect(() => { setCurrentMatch(0) }, [searchQuery])

  useEffect(() => {
    if (firstTargetIdx !== -1 && targetScrollRef.current) {
      targetScrollRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [scrollTrigger, xml, firstTargetIdx])

  useEffect(() => {
    if (activeMatchIndices.length > 0) {
      matchScrollRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [safeMatch, activeMatchIndices])

  const prevMatch = () => setCurrentMatch(m => (m - 1 + activeMatchIndices.length) % activeMatchIndices.length)
  const nextMatch = () => setCurrentMatch(m => (m + 1) % activeMatchIndices.length)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); nextMatch() }
    else if (e.key === 'Escape') setSearchQuery('')
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-gray-600 shrink-0">{DOC_LABELS[docType]}</span>
          {xpath && firstTargetIdx === -1 && !loading && xmlLines.length > 0 && (
            <span className="text-xs text-gray-400 shrink-0">— {t('xml_panel.not_found')}</span>
          )}
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('common.search_placeholder')}
              className="text-xs border border-gray-200 rounded px-2 py-0.5 flex-1 min-w-0 max-w-[140px] focus:outline-none focus:border-blue-400 bg-white dark:bg-gray-700 dark:border-gray-600"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="shrink-0 text-gray-400 hover:text-gray-600 text-sm leading-none" title={t('xml_panel.clear_search')}>×</button>
            )}
            {activeMatchIndices.length > 0 && (
              <>
                <span className="shrink-0 text-xs text-gray-400 tabular-nums">{safeMatch + 1}/{activeMatchIndices.length}</span>
                <button onClick={prevMatch} className="shrink-0 text-xs text-gray-500 hover:text-gray-800 px-0.5" title={t('xml_panel.previous_match')}>↑</button>
                <button onClick={nextMatch} className="shrink-0 text-xs text-gray-500 hover:text-gray-800 px-0.5" title={t('xml_panel.next_match')}>↓</button>
              </>
            )}
            {searchQuery && activeMatchIndices.length === 0 && (
              <span className="shrink-0 text-xs text-red-400">{t('common.no_results')}</span>
            )}
          </div>
          <button
            onClick={() => setRawMode(m => !m)}
            className={`shrink-0 text-xs px-2 py-0.5 rounded border transition-colors ${rawMode ? 'bg-blue-100 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}
            title={rawMode ? t('xml_panel.show_formatted') : t('xml_panel.show_original')}
          >
            {rawMode ? t('xml_panel.formatted') : t('xml_panel.original')}
          </button>
        </div>
      </div>

      <div className="overflow-auto" style={{ maxHeight }}>
        {loading && <div className="p-3 text-xs text-gray-400">{t('common.loading')}</div>}
        {error   && <div className="p-3 text-xs text-red-500">{error}</div>}
        {!loading && !error && xml && !rawMode && xmlLines.length === 0 && (
          <div className="p-3 text-xs text-red-500">{t('xml_panel.parse_error')}</div>
        )}

        {!loading && !error && rawMode && rawLines.length > 0 && (
          <div className="text-xs font-mono leading-5 p-2 select-text">
            {rawLines.map((line, i) => {
              const isCurrentMatch = rawMatchIndices[safeMatch] === i
              const isAnyMatch     = rawMatchIndices.includes(i)
              const bgClass = isCurrentMatch ? 'bg-orange-100' : isAnyMatch ? 'bg-orange-50' : ''
              const theRef = isCurrentMatch ? matchScrollRef : undefined
              return (
                <div key={i} ref={theRef as React.RefObject<HTMLDivElement>} className={`flex items-baseline px-1 ${bgClass}`}>
                  <span className="select-none text-gray-300 text-right pr-3 shrink-0 tabular-nums" style={{ minWidth: '3rem' }}>{i + 1}</span>
                  <span className="whitespace-pre text-gray-700">
                    {searchQuery ? <HighlightedText text={line} query={searchQuery} /> : line}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {!loading && !error && !rawMode && xmlLines.length > 0 && (
          <div className="text-xs font-mono leading-5 p-2 select-text">
            {xmlLines.map((line, i) => {
              const isCurrentMatch = matchIndices[safeMatch] === i
              const isAnyMatch     = matchIndices.includes(i)
              const bgClass = isCurrentMatch ? 'bg-orange-100' : isAnyMatch ? 'bg-orange-50' : line.isTarget ? 'bg-yellow-100' : ''
              const theRef = isCurrentMatch ? matchScrollRef : (i === firstTargetIdx ? targetScrollRef : undefined)
              return (
                <span key={i} ref={theRef} className={`flex items-baseline px-1 ${bgClass} ${line.isSummary ? 'italic text-gray-400' : ''}`}>
                  <span className="shrink-0 w-4 text-center select-none mr-0.5">
                    {line.nodeId && !line.isSummary && (
                      <button onClick={() => toggleCollapse(line.nodeId!)} className="text-gray-400 hover:text-blue-500 leading-none" title={line.collapsed ? t('xml_panel.expand') : t('xml_panel.collapse')}>
                        {line.collapsed ? '▶' : '▼'}
                      </button>
                    )}
                    {line.isSummary && (
                      <button
                        onClick={() => toggleCollapse(line.nodeId!)}
                        className="text-gray-400 hover:text-blue-500 leading-none"
                        title={line.isBase64Summary ? t('xml_panel.show_base64') : t('xml_panel.expand')}
                      >▶</button>
                    )}
                  </span>
                  <span className="whitespace-pre">
                    {searchQuery ? <HighlightedText text={line.text} query={searchQuery} /> : line.text}
                  </span>
                </span>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
